import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowDownRight, ArrowUpRight, Minus, TrendingUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { formatCurrency, formatCurrencyNoCents } from '../../lib/formatters';
import { buildTrendSeries, shortDate, weeklyCompare } from './digestTrendsModel';

// Phase 6 Part 2 (10/07) — weekly KPI row with trend arrows (2b) and the
// funded-vs-collected trend chart (2a), both built ONLY from the
// portfolio_digest daily series in job_reports. No transaction re-aggregation;
// shallow history renders what exists and says so.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERIES_DAYS = 200; // > 6 months of daily docs

const Arrow = ({ arrow, good }) => {
  if (arrow === 'flat') return <Minus className="h-4 w-4 text-gray-400" />;
  const Icon = arrow === 'up' ? ArrowUpRight : ArrowDownRight;
  return <Icon className={`h-4 w-4 ${good ? 'text-emerald-600' : 'text-red-600'}`} />;
};

const KpiTile = ({ m }) => {
  const fmt = (v) => (m.kind === 'money' ? formatCurrency(v) : v.toLocaleString('en-US'));
  const good = m.arrow === 'flat' ? true : (m.arrow === 'up') === m.higherIsBetter;
  const pct = m.pct === null ? 'no prior-week baseline' : `${m.pct >= 0 ? '+' : ''}${m.pct.toFixed(0)}% vs last week`;
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3" data-testid={`weekly-kpi-${m.key}`}>
      <div className="flex items-center justify-between">
        <p className="text-2xl font-bold text-gray-900">{fmt(m.current)}</p>
        <Arrow arrow={m.arrow} good={good} />
      </div>
      <p className="text-xs uppercase tracking-wide text-gray-500 mt-1">{m.label} this week</p>
      <p className="text-xs text-gray-400">{pct} · last week {fmt(m.previous)}</p>
    </div>
  );
};

const DigestTrends = ({ getAuthHeaders, tenant = 'live' }) => {
  const [payload, setPayload] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const res = await axios.get(`${API}/reports/portfolio-digest`, {
          params: { days: SERIES_DAYS },
          headers: { ...getAuthHeaders(), 'X-Tenant': tenant },
        });
        if (!cancelled) setPayload(res.data);
      } catch (err) {
        if (!cancelled) {
          const detail = err.response?.data?.detail;
          setLoadError(typeof detail === 'string' ? detail : (err.message || 'request failed'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getAuthHeaders, tenant]);

  const reports = useMemo(() => payload?.reports || [], [payload]);
  const yesterday = payload?.yesterday;
  const series = useMemo(() => buildTrendSeries(reports, { months: 6, yesterday }), [reports, yesterday]);
  const weekly = useMemo(() => weeklyCompare(reports, yesterday), [reports, yesterday]);

  const title = (
    <CardHeader className="pb-3">
      <CardTitle className="flex items-center gap-2 text-base">
        <TrendingUp className="h-4 w-4 text-[#3b82f6]" />
        Funded vs collected
        <span className="text-xs font-normal text-gray-500">from the daily digest series</span>
      </CardTitle>
    </CardHeader>
  );

  if (loading) {
    return <Card className="mb-6" data-testid="digest-trends">{title}<CardContent><p className="text-sm text-gray-500">Loading digest history…</p></CardContent></Card>;
  }
  if (loadError) {
    return <Card className="mb-6" data-testid="digest-trends">{title}<CardContent><p className="text-sm text-red-700">Couldn't load digest history: {loadError}</p></CardContent></Card>;
  }
  if (!series.range) {
    return (
      <Card className="mb-6" data-testid="digest-trends" data-state="empty">
        {title}
        <CardContent>
          <p className="text-sm text-gray-700 font-medium">No digest history yet</p>
          <p className="text-sm text-gray-500">
            Trends appear once portfolio_digest has stored at least one day.{' '}
            <Link to="/admin/jobs" className="text-[#3b82f6] hover:underline">Open Admin → Jobs</Link>
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-6" data-testid="digest-trends" data-state="ok">
      {title}
      <CardContent className="space-y-4">
        {weekly && (
          <div>
            <div className="flex flex-wrap items-baseline gap-x-2 text-xs text-gray-500 mb-2">
              <span className="uppercase tracking-wide">This week</span>
              <span>
                {shortDate(weekly.thisWeek.from)}–{shortDate(weekly.thisWeek.to)} vs {shortDate(weekly.lastWeek.from)}–{shortDate(weekly.lastWeek.to)}
                {' '}(same weekdays)
              </span>
              {!weekly.complete && (
                <span className="text-amber-700">
                  · {weekly.thisWeek.daysPresent}/{weekly.thisWeek.daysExpected} and {weekly.lastWeek.daysPresent}/{weekly.lastWeek.daysExpected} digest days present
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {weekly.metrics.map((m) => <KpiTile key={m.key} m={m} />)}
            </div>
          </div>
        )}

        <div>
          <div className="flex flex-wrap items-center gap-x-2 text-xs text-gray-500 mb-1">
            <span>{series.rangeLabel}</span>
            <span>· {series.granularity === 'week' ? 'weekly totals, weeks start Monday' : 'daily totals'}</span>
            {series.shallow && <span className="text-amber-700">· history is shorter than 6 months; nothing is back-filled</span>}
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={series.points} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v) => formatCurrencyNoCents(v)} tick={{ fontSize: 11 }} width={80} />
              <Tooltip
                formatter={(value, name) => [formatCurrency(value), name]}
                labelFormatter={(label, items) => {
                  const p = items && items[0] && items[0].payload;
                  if (!p) return label;
                  return series.granularity === 'week'
                    ? `Week of ${label}${p.partial ? ` (${p.days} day${p.days === 1 ? '' : 's'})` : ''}`
                    : label;
                }}
              />
              <Legend />
              <Line type="monotone" dataKey="funded" stroke="#3b82f6" name="Funded" strokeWidth={2} dot={series.granularity === 'day'} />
              <Line type="monotone" dataKey="collected" stroke="#10b981" name="Collected" strokeWidth={2} dot={series.granularity === 'day'} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default DigestTrends;
